import { isUndefined } from 'lodash';
import { memo, useMemo } from 'react';

import { MessageCircleIcon } from '@air/design-icons';

import { getPageNumber, isAnnotationCorrect } from '~/components/Annotations/shared/annotationUtils';
import { type ClipDiscussion } from '~/components/Annotations/shared/types';
import {
  DiscussionMetadata,
  type DiscussionMetadataProps,
} from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/DiscussionMetadata';
import { formatDiscussionTimestamp } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/formatDiscussionTimestamp';
import { ANNOTATION_PAGE_NUMBER, DISCUSSION_HEADER_TIMESTAMP_PILL } from '~/constants/testIDs';

export type DiscussionAnnotationInfoProps = Pick<DiscussionMetadataProps, 'color'> & {
  discussion: ClipDiscussion;
  endTimestamp?: number;
};

export const DiscussionAnnotationInfo = memo(
  ({ discussion: { annotation }, color, endTimestamp }: DiscussionAnnotationInfoProps) => {
    const timestamp = annotation?.timestamp;
    const pageNumber = getPageNumber(annotation);

    const textInfo = useMemo(() => {
      if (!isUndefined(timestamp)) {
        return {
          info: formatDiscussionTimestamp({ endTimestamp, timestamp }),
          textTestId: DISCUSSION_HEADER_TIMESTAMP_PILL,
        };
      } else if (!isUndefined(pageNumber) && pageNumber !== -1) {
        return {
          info: `pg.${pageNumber}`,
          textTestId: ANNOTATION_PAGE_NUMBER,
        };
      }
    }, [endTimestamp, pageNumber, timestamp]);

    if (!isAnnotationCorrect(annotation)) {
      return null;
    }

    return (
      <DiscussionMetadata
        Icon={MessageCircleIcon}
        // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
        text={textInfo?.info}
        color={color}
        // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
        textTestId={textInfo?.textTestId}
      />
    );
  },
);

DiscussionAnnotationInfo.displayName = 'DiscussionAnnotationInfo';
